/**
 * Location Labels Field
 *
 * @author Jacob Fancher (jfancher@yext.com)
 */
goog.provide('yext.location.labels');

goog.require('yext');

goog.scope(function() {
  'use strict';

  var _labels = yext.location.labels;

  /**
   * Prints the name of a label, including the internal indicator if it's an internal label.
   *
   * @param label {!Object}  The label to print
   * @return      {string}  The formatted label name
   */
  var printLabel = function(label) {
    var text = label.name;
    if (label.internal) {
      text = text + ' *';
    }
    return text;
  };

  /**
   * View model for location labels presenters.
   *
   * @param labels      {array}   The list of labels
   * @param elidedCount {number}  The number of elided selected labels, if any
   * @constructor
   */
  var LabelsViewModel = function(labels, elidedCount) {
    this._labels = labels;
    this._elidedCount = elidedCount;
    this._sort();

    if (this.isElided()) {
      this._labels.forEach(function(label) {
        if (label.selected) {
          label.selected = false;
        }
      });
    }
  };

  /**
   * Sorts the label collection.
   */
  LabelsViewModel.prototype._sort = function() {
    this._labels.sort(function(a, b) {
      return a.name.localeCompare(b.name, {sensitivity: 'base'});
    });
  };

  /**
   * Gets the collection of labels.
   *
   * @param   {!Object}  filter  Optional selected, visible, and indeterminate flags for filtering
   * @return  {array}           The list of labels
   */
  LabelsViewModel.prototype.labels = function(filter) {
    if (!filter) {
      return this._labels;
    }
    return this._labels.filter(function(label) {
      return LabelsViewModel._filter(label, filter);
    });
  };

  /**
   * Gets whether or not the label selection is elided.
   *
   * @return {boolean}  Whether or not the selection is elided
   */
  LabelsViewModel.prototype.isElided = function() {
    return this._elidedCount > 0;
  };

  /**
   * Gets the number of elided selected labels.
   *
   * @return {number} The elided selection count
   */
  LabelsViewModel.prototype.getElidedCount = function() {
    return this._elidedCount;
  };

  /**
   * Sets the number of elided selected labels.
   *
   * @param {number}  count The elided selection count
   */
  LabelsViewModel.prototype.setElidedCount = function(count) {
    this._elidedCount = count;
    this._broadcast();
  };

  /**
   * Determines if any labels match the given filter.
   *
   * @param   {!Object}  filter  Selected, visible, and indeterminate flags for filtering
   * @return  {boolean}         Whether or not any label matches the specified flags
   */
  LabelsViewModel.prototype.any = function(filter) {
    return this._labels.some(function(label) {
      return LabelsViewModel._filter(label, filter);
    });
  };

  /**
   * Selects a label given its name. If no label with the specified name exists, a new (public)
   * label is created.
   *
   * @param {string}  name  The label's name
   */
  LabelsViewModel.prototype.select = function(name) {
    var label = this.find(name);
    if (label) {
      label.selected = true;
      label.indeterminate = false;
    } else {
      label = {name: name, internal: false, visible: true, selected: true, indeterminate: false};
      this._labels.push(label);
      this._sort();
    }
    this._broadcast();
  };

  /**
   * Deselects a label given its name. If the label is a newly-created one (without an id), it is
   * removed from the label collection.
   *
   * @param {string}  name  The label's name
   */
  LabelsViewModel.prototype.deselect = function(name) {
    var label = this.find(name);
    if (label) {
      label.selected = false;
      label.indeterminate = false;

      if (!label.id) {
        this._labels.splice(this._labels.indexOf(label), 1);
      }

      this._broadcast();
    }
  };

  /**
   * Finds the label with the specified name.
   *
   * @param   {string}  name  The label's name
   * @return  {!Object}        The label, if it exists
   */
  LabelsViewModel.prototype.find = function(name) {
    return this._labels.find(function(label) {
      return label.name === name;
    });
  };

  /** Broadcasts a label update event. */
  LabelsViewModel.prototype._broadcast = function() {
    $(this).trigger('update.labels');
  };

  /**
   * Applies a filter to a label.
   *
   * @param   {!Object}  label   The label
   * @param   {!Object}  filter  The filter
   * @return  {boolean}         Whether or not the label passes the filter
   */
  LabelsViewModel._filter = function(label, filter) {
    return !((filter.selected === true && !label.selected)
             || (filter.selected === false && label.selected)
             || (filter.visible === true && !label.visible)
             || (filter.visible === false && label.visible)
             || (filter.indeterminate === true && !label.indeterminate)
             || (filter.indeterminate === false && label.indeterminate));
  };

  /**
   * Renders an editor for a location labels field.
   *
   * @param $host   {!jQuery}  The selector for the element which hosts the field
   * @param model   {!Object}        LabelsViewModel for the field
   * @param options {!Object}        Configuration options for the editor
   * @constructor
   */
  var LabelsFieldEditor = function($host, model, options) {
    this.$host = $host;
    this.model = model;
    this.canCreate = options.canCreate;
    this.$originalData = $host.find('.js-original-selection-data');
    this.$updatedData = $host.find('.js-updated-selection-data');

    var self = this;
    this.createSearcher();
    this.saveSelection(this.$originalData);
    this.saveSelection(this.$updatedData);
    $(this.model).on('update.labels', function() {
      self.saveSelection(self.$updatedData);
    });

    $host.find('.js-label-toggle').each(function() {
      self.bindToggle($(this));
    });
  };

  /**
   * Creates (or re-creates) the label searcher.
   */
  LabelsFieldEditor.prototype.createSearcher = function() {
    var self = this;
    var indeterminate = this.model.any({visible: true, indeterminate: true});
    var placeholder = indeterminate ? yext.msg('Add more labels here') : yext.msg('Add labels');

    self.$searcher = $('<input type="text">');
    self.$host.find('.js-label-searcher').empty().append(self.$searcher);

    var searchLabels = self.model.labels({visible: true, indeterminate: false});
    var search = function(term) {
      term = term.replace(/[\-\[\]\/\{\}\(\)\*\+\?\.\\\^\$\|]/g, '\\$&');
      var re = new RegExp(term, 'i');
      return searchLabels.filter(function(label) {
        return !label.selected && label.name.search(re) !== -1;
      })
        .map(function(label) {
          return {label: printLabel(label), value: label.name};
        });
    };

    self.$searcher.tagsInput({
      createNewTags: self.canCreate,
      customClass: 'badge-label',
      autocomplete: {
        source: function(request, response) {
          response(search(request.term));
        },
      },
      defaultText: placeholder,
      placeholderColor: '#a9a9a4',
      onAddTag: function(value) {
        self.model.select(value);
      },
      onRemoveTag: function(value) {
        self.model.deselect(value);
      },
      formatValue: function(value) {
        var label = self.model.find(value);
        return label ? printLabel(label) : value;
      },
    });

    if (!indeterminate) {
      self.model
        .labels({visible: true, selected: true})
        .forEach(function(label) {
          self.$searcher.addTag(label.name);
        });
    }
  };

  /**
   * Saves the current selection as the value of a form input.
   *
   * @param $target {!jQuery}  The selector for the form input
   */
  LabelsFieldEditor.prototype.saveSelection = function($target) {
    var value = {
      selected: this.model.labels({selected: true}),
      elidedCount: this.model.getElidedCount(),
    };
    $target.val(yext.JSON.stringify(value));
  };

  /**
   * Binds a toggle button for a label in indeterminate edit mode.
   *
   * @param $toggle {!jQuery}  The selector for the checkbox
   */
  LabelsFieldEditor.prototype.bindToggle = function($toggle) {
    var self = this;
    var name = $toggle.data('label-name');
    var label = self.model.find(name);

    $toggle.prop('checked', label.selected && !label.indeterminate);
    $toggle.prop('indeterminate', label.selected && label.indeterminate);
    $toggle.change(function() {
      $toggle.is(':checked') ? self.model.select(name) : self.model.deselect(name);
    });
  };

  /**
   * Clears the editor's original selection. No original selection will be sent to the server,
   * signifying an unknown initial state (which changes how the update application is generated).
   * Used specifically in applying a template, where the original profile values are never made
   * known ahead of time.
   */
  LabelsFieldEditor.prototype.clearOriginalSelection = function() {
    this.$originalData.val(null);
  };

  /**
   * Renders the full field view of a location labels field, adding dynamic saving and reloading
   * support to the basic editor.
   *
   * @param $host   {!jQuery}  The selector for the element which hosts the field
   * @param model   {!Object}        LabelsViewModel for the field
   * @param options {!Object}        Options for the field
   * @param field   {LocationField} The location field object for location labels
   * @constructor
   */
  var LabelsFieldView = function($host, model, options, field) {
    this.$host = $host;
    this.canCreate = options.canCreate;
    this.readOnly = options.readOnly;
    this.field = field;
    this.reset(model);

    var self = this;
    $(this.field.$form).on('save.field', function(e, response) {
      if (response.field) {
        self.reset(new LabelsViewModel(response.field.labels.all, response.field.elidedCount));
      }
    });
    this.field.$edit.find('.js-cancel-edit').click(function() {
      self.reset(self.initialModel);
    });
  };

  /**
   * Re-renders the field.
   *
   * @param model {!Object}  The new LabelsViewModel
   */
  LabelsFieldView.prototype.reset = function(model) {
    this.initialModel = model;
    this.model = $.extend(true, {}, model);
    this.renderView();
    this.renderElision();
    this.renderIndeterminate();
    this.editor = new LabelsFieldEditor(this.$host, this.model, {canCreate: this.canCreate});

    var self = this;
    $(this.model).on('update.labels', function() {
      self.field.field.enableSubmit();
      self.field.hideAllErrors();
    });
  };


  /**
   * Renders the field view mode.
   */
  LabelsFieldView.prototype.renderView = function() {
    var self = this;
    var indeterminate = this.model.any({selected: true, indeterminate: true, visible: true});
    var empty = !this.model.any({selected: true, visible: true});
    var $values = this.$host.find('.js-labels-values');
    var $various = this.$host.find('.js-labels-various');
    var $empty = this.$host.find('.js-labels-emptyvalue');

    $values.empty();
    self.model.labels({selected: true, visible: true}).forEach(function(label) {
      $('<span>').addClass('badge-label')
        .addClass('label-view')
        .text(printLabel(label))
        .appendTo($values);
    });

    $various.toggle(indeterminate);
    $empty.toggle(empty && !this.readOnly && !this.model.isElided());
    $values.toggle(!empty && !indeterminate && !this.model.isElided());
  };

  /**
   * Renders the field elision state (both view and edit mode).
   */
  LabelsFieldView.prototype.renderElision = function() {
    var self = this;
    var $elided = self.$host.find('.js-labels-elided');
    var $count = self.$host.find('.js-labels-num-elided');

    $elided.toggle(self.model.isElided());
    $count.text(yext.msgn('1 label', '{0} labels', self.model.getElidedCount()));

    $elided.find('.js-remove-elided').click(function() {
      self.model.setElidedCount(0);
      $elided.hide();
    });
  };

  /**
   * Renders the field indeterminate edit mode.
   */
  LabelsFieldView.prototype.renderIndeterminate = function() {
    var self = this;
    var indeterminate = this.model.any({selected: true, indeterminate: true, visible: true});
    var $host = this.$host.find('.js-labels-indeterminate');
    var $various = this.$host.find('.js-labels-various');

    $various.find('.js-labels-various').toggle(indeterminate);
    $host.toggle(indeterminate);
    $host.empty();

    if (indeterminate) {
      this.model.labels({selected: true, visible: true}).forEach(function(label) {
        var $toggle = $('<input type="checkbox">');
        $toggle.addClass('js-label-toggle')
          .data('label-name', label.name);

        var $div = $('<div>');
        $div.append($toggle)
          .append($('<span>').text(printLabel(label)));

        $host.append($div);
      });
    }
  };

  /**
   * Creates a full field view for a location labels field.
   *
   * @param $host {!jQuery} The selector for the element which hosts the field
   * @param field {LocationField} Location field object for location labels
   */
  _labels.createFieldView = function($host, field) {
    var model = $host.data('model');
    var vm = new LabelsViewModel(model.labels.all, model.elidedCount);
    var options = {
      canCreate: model.canCreate,
      readOnly: model.readOnly,
    };
    var view = new LabelsFieldView($host, vm, options, field);
    return vm;
  };

  /**
   * Creates an editor-only field view for a location labels field.
   *
   * @param $host {!jQuery} The selector for the element which hosts the editor
   */
  _labels.createFieldEditor = function($host) {
    var model = $host.data('model');
    var vm = new LabelsViewModel(model.labels.all, model.elidedCount);
    var editor = new LabelsFieldEditor($host, vm, {canCreate: model.canCreate});
    return editor;
  };
});
